import React, { useState, useEffect } from 'react';
import CustomButton from './CustomButton';
import { FunnelIcon, XMarkIcon, AdjustmentsHorizontalIcon, CalendarIcon } from '@heroicons/react/24/outline';
import StyledSelect from './StyledSelect';
import { SLIDE_OVER_TOP_OFFSET_STYLE } from './slideOverTransition';

function WarningIconWithTooltip({ tooltip }) {
  return (
    <div className="relative flex items-center group cursor-pointer">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500 dark:text-amber-400" viewBox="0 0 24 24" fill="currentColor">
        <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
      </svg>

      <div className="absolute z-10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity
                      bg-gray-800 text-white text-xs rounded-md px-2 py-1 left-1/2 transform -translate-x-1/2 -top-10
                      whitespace-nowrap pointer-events-none shadow-lg dark:bg-gray-700">
        {tooltip}
      </div>
    </div>
  );
}

export default function PaymentsActionsPanel({
                                               year,
                                               month,
                                               appliedYear,
                                               appliedMonth,
                                               paymentRequirements,
                                               hasPaymentsData = true,
                                               onYearChange,
                                               onMonthChange,
                                               options = [],
                                               selectedOption,
                                               onOptionChange,
                                               onApply = () => {},
                                               bare = false,
                                             }) {

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 10}, (_, i) => currentYear + 4 - i);
  const months = Array.from({length: 12}, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: new Date(0, i).toLocaleString('default', {month: 'long'}),
  }));

  // Combined month-year options for bare/compact mode (last 3 years)
  const now = new Date();
  const combinedMonthYearOptions = Array.from({length: 36}, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = String(d.getFullYear());
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return {
      value: `${y}-${m}`,
      label: `${new Date(d.getFullYear(), d.getMonth()).toLocaleString('default', {month: 'long'})} ${y}`,
    };
  });

  // Get month name for display
  const getMonthName = (monthValue) => {
    if (!monthValue) return '';
    const monthObj = months.find(m => m.value === monthValue);
    return monthObj ? monthObj.label : '';
  };

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isFilterPanelOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFilterPanelOpen]);

  const content = (
    <>
      {/* Mobile: Compact layout with filter button */}
      <div className="sm:hidden space-y-3">
        {/* Viewing Info and Warning */}
        <div className="space-y-2">
          {appliedYear && appliedMonth && (
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-brand-600 dark:text-brand-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                : <span className="text-brand-600 dark:text-brand-400">{getMonthName(appliedMonth)} {appliedYear}</span>
              </span>
            </div>
          )}

          {hasPaymentsData && paymentRequirements && !paymentRequirements.full_month && (
            <div className="flex items-center gap-2">
              <WarningIconWithTooltip
                tooltip={
                  <>
                    <p className="text-white">There are missing scores in the month</p>
                    <p className="text-gray-200 text-xs">
                      Scores: {paymentRequirements.max_nr_scores} / {paymentRequirements.max_nr_expected_scores}
                    </p>
                  </>
                }
              />
              <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                Incomplete Month: Missing {paymentRequirements.max_nr_expected_scores - paymentRequirements.max_nr_scores} score(s)
              </span>
            </div>
          )}
        </div>

        {/* Filter Button */}
        <button
          onClick={() => setIsFilterPanelOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <FunnelIcon className="h-5 w-5" />
          <span className="text-sm font-medium">
            {selectedOption && options.length > 0 ? `${selectedOption.charAt(0).toUpperCase() + selectedOption.slice(1)} • ` : ''}
            {getMonthName(month)} {year}
          </span>
        </button>

        {/* Apply Button — bare mode applies filters as soon as they change */}
        {!bare && (
          <CustomButton
            caption="Apply Filters"
            icon="search"
            size="medium"
            onClick={onApply}
            className="w-full"
          />
        )}
      </div>

      {/* Filter Side Panel (Mobile) */}
      {isFilterPanelOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsFilterPanelOpen(false)} />
          <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white dark:bg-gray-870 shadow-xl z-50 overflow-y-auto" style={SLIDE_OVER_TOP_OFFSET_STYLE}>
            <div className="sticky top-0 bg-surface dark:bg-gray-870 border-b border-gray-200/70 dark:border-gray-600/70 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Payment Filters</h2>
              <button
                onClick={() => setIsFilterPanelOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              {/* Payment Track */}
              {options.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Payment Track
                  </label>
                  <div className="space-y-2">
                    {options.map((opt) => (
                      <div
                        key={opt}
                        onClick={() => {
                          onOptionChange({ target: { value: opt } });
                        }}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedOption === opt
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                            : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </div>
                          {selectedOption === opt && (
                            <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                              <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Year
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {years.map((y) => (
                    <div
                      key={y}
                      onClick={() => onYearChange(String(y))}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        year === String(y)
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                          : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{y}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Month */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Month
                </label>
                <div className="space-y-2">
                  {months.map(({ value, label }) => (
                    <div
                      key={value}
                      onClick={() => onMonthChange(value)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        month === value
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                          : 'border-gray-200/70 dark:border-gray-600/70 hover:border-brand-300 dark:hover:border-brand-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</div>
                        {month === value && (
                          <div className="h-5 w-5 rounded-full bg-brand-600 flex items-center justify-center">
                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Done Button */}
              <button
                onClick={() => setIsFilterPanelOpen(false)}
                className="w-full px-4 py-2 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 text-sm font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </>
      )}

      {/* Desktop: layout */}
      <div className={`hidden sm:flex gap-3 ${bare ? 'flex-col' : 'items-center flex-wrap justify-between'}`}>
        {/* Bare mode: the period selector below already names the month being
            viewed, so only the incomplete-month warning needs a line here. */}
        {bare && hasPaymentsData && paymentRequirements && !paymentRequirements.full_month && (
          <div className="flex items-center gap-1.5">
            <WarningIconWithTooltip
              tooltip={
                <>
                  <p className="text-white">There are missing scores in the month</p>
                  <p className="text-gray-200 text-xs">
                    Scores: {paymentRequirements.max_nr_scores} / {paymentRequirements.max_nr_expected_scores}
                  </p>
                </>
              }
            />
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap">
              Missing {paymentRequirements.max_nr_expected_scores - paymentRequirements.max_nr_scores} score(s)
            </span>
          </div>
        )}

        {/* Non-bare: viewing info block */}
        {!bare && (
          <div className="flex flex-col gap-1.5">
            {appliedYear && appliedMonth && (
              <div>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Viewing</span>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mt-0.5">
                  {getMonthName(appliedMonth)} {appliedYear}
                </p>
              </div>
            )}
            {hasPaymentsData && paymentRequirements && !paymentRequirements.full_month && (
              <div className="flex items-center gap-1.5 mt-1">
                <WarningIconWithTooltip
                  tooltip={
                    <>
                      <p className="text-white">There are missing scores in the month</p>
                      <p className="text-gray-200 text-xs">
                        Scores: {paymentRequirements.max_nr_scores} / {paymentRequirements.max_nr_expected_scores}
                      </p>
                    </>
                  }
                />
                <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                  Missing {paymentRequirements.max_nr_expected_scores - paymentRequirements.max_nr_scores} score(s)
                </span>
              </div>
            )}
          </div>
        )}

        {/* Filters row */}
        <div className={bare ? 'grid grid-cols-2 gap-2' : 'flex items-center flex-wrap gap-3'}>
          {/* Metric Picker */}
          {options.length > 0 && (
            bare ? (
              <StyledSelect
                icon={AdjustmentsHorizontalIcon}
                value={selectedOption}
                onChange={onOptionChange}
                options={options.map((opt) => ({ value: opt, label: opt.charAt(0).toUpperCase() + opt.slice(1) }))}
                className="w-full"
              />
            ) : (
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Payment Track</label>
                <select
                  className="w-40 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  value={selectedOption}
                  onChange={onOptionChange}
                >
                  {options.map((opt) => (
                    <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                  ))}
                </select>
              </div>
            )
          )}

          {bare ? (
            /* Combined month+year picker */
            <StyledSelect
              icon={CalendarIcon}
              value={`${year}-${month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-');
                onYearChange(y);
                onMonthChange(m);
              }}
              options={combinedMonthYearOptions}
              className="w-full"
            />
          ) : (
            <>
              {/* Year Picker */}
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Year</label>
                <select
                  className="w-24 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 px-3 py-2"
                  onChange={(e) => onYearChange(e.target.value)}
                  value={year}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {/* Month Picker */}
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Month</label>
                <select
                  className="w-32 rounded-lg border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 px-3 py-2"
                  value={month}
                  onChange={(e) => onMonthChange(e.target.value)}
                >
                  {months.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Apply Button — bare mode applies filters as soon as they change */}
          {!bare && (
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 opacity-0">Apply</label>
              <CustomButton caption="Apply Filters" icon="search" size="medium" onClick={onApply} />
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (bare) return content;
  return (
    <div className="bg-surface dark:bg-gray-870 rounded-xl border border-gray-200/70 dark:border-gray-600/70 shadow-sm p-4 sm:p-6">
      {content}
    </div>
  );
}